import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

// Layouts
import B2BLayout from './layouts/B2BLayout';
import B2CLayout from './layouts/B2CLayout';

import LoadingSpinner from '../../shared/components/LoadingSpinner/LoadingSpinner.js';
import NavigationProgress from './components/common/NavigationProgress';
import NetworkStatusHandler from './components/common/NetworkStatusHandler';
import { autoPrefetchLinks, prefetchRoute } from './prefetch/routes';

// Lazy-loaded page components (route-level code splitting)
const Home = lazy(() => import('./pages/Home'));
const ProductCatalog = lazy(() => import('./pages/ProductCatalog'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const Profile = lazy(() => import('./pages/auth/Profile'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const B2BApplication = lazy(() => import('./pages/b2b/B2BApplication'));
const B2BDashboard = lazy(() => import('./pages/b2b/B2BDashboard'));
const B2BQuickOrder = lazy(() => import('./pages/b2b/B2BQuickOrder'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Bundles = lazy(() => import('./pages/Bundles'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Compare = lazy(() => import('./pages/Compare'));
const FurnitureOrderTrackerDemo = lazy(
  () => import('./pages/FurnitureOrderTrackerDemo')
);
const SetConfigurator = lazy(
  () => import('./components/configurator/SetConfigurator')
);

// Static pages
const Help = lazy(() => import('./pages/static/Help'));
const Returns = lazy(() => import('./pages/static/Returns'));
const Privacy = lazy(() => import('./pages/static/Privacy'));
const Terms = lazy(() => import('./pages/static/Terms'));
const FAQ = lazy(() => import('./pages/static/FAQ'));
const About = lazy(() => import('./pages/static/About'));
const Delivery = lazy(() => import('./pages/static/Delivery'));
const Warranty = lazy(() => import('./pages/static/Warranty'));
const Contacts = lazy(() => import('./pages/static/Contacts'));

// Blog pages
const Blog = lazy(() => import('./pages/blog/Blog'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));

// Context hooks
import { useAuth } from './context/AuthContext';

// Protected route component (uses shared implementation)
import ProtectedRoute from './components/ProtectedRoute';

// Adaptive route for B2C/B2B layout switching
import AdaptiveRoute from './components/routing/AdaptiveRoute';

function App() {
  const { isLoading } = useAuth();

  // Prefetch critical routes during idle time
  useEffect(() => {
    const idle =
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback
        : (cb) => setTimeout(cb, 1);

    idle(() => {
      // Prefetch high-priority routes
      prefetchRoute('/');
      prefetchRoute('/products');
      prefetchRoute('/cart');
    });

    // Auto-prefetch links when they enter viewport
    const cleanup = autoPrefetchLinks(document);
    return cleanup;
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <NavigationProgress />
      <NetworkStatusHandler />
      <Routes>
        {/* Public routes with B2C layout */}
        <Route
          path="/"
          element={
            <B2CLayout>
              <Home />
            </B2CLayout>
          }
        />

        <Route
          path="/products"
          element={
            <B2CLayout>
              <ProductCatalog />
            </B2CLayout>
          }
        />

        <Route
          path="/products/:slug"
          element={
            <B2CLayout>
              <ProductDetail />
            </B2CLayout>
          }
        />

        <Route
          path="/bundles"
          element={
            <B2CLayout>
              <Bundles />
            </B2CLayout>
          }
        />

        <Route
          path="/favorites"
          element={
            <B2CLayout>
              <Favorites />
            </B2CLayout>
          }
        />

        <Route
          path="/compare"
          element={
            <B2CLayout>
              <Compare />
            </B2CLayout>
          }
        />

        <Route
          path="/set-configurator"
          element={
            <B2CLayout>
              <SetConfigurator />
            </B2CLayout>
          }
        />

        <Route
          path="/cart"
          element={
            <B2CLayout>
              <Cart />
            </B2CLayout>
          }
        />

        {/* Authentication routes */}
        <Route
          path="/login"
          element={
            <B2CLayout>
              <Login />
            </B2CLayout>
          }
        />

        <Route
          path="/register"
          element={
            <B2CLayout>
              <Register />
            </B2CLayout>
          }
        />

        {/* Organization onboarding route */}
        <Route
          path="/onboarding"
          element={
            <B2CLayout>
              <Onboarding />
            </B2CLayout>
          }
        />

        {/* Alias for onboarding */}
        <Route path="/signup" element={<Navigate to="/onboarding" replace />} />

        {/* Protected user routes using AdaptiveRoute */}
        <Route
          path="/profile"
          element={
            <AdaptiveRoute requireAuth>
              <Profile />
            </AdaptiveRoute>
          }
        />

        <Route
          path="/checkout"
          element={
            <AdaptiveRoute requireAuth>
              <Checkout />
            </AdaptiveRoute>
          }
        />

        <Route
          path="/orders"
          element={
            <AdaptiveRoute requireAuth>
              <OrderHistory />
            </AdaptiveRoute>
          }
        />

        <Route
          path="/orders/:orderId"
          element={
            <AdaptiveRoute requireAuth>
              <OrderDetail />
            </AdaptiveRoute>
          }
        />

        {/* Public order tracking route */}
        <Route
          path="/track"
          element={
            <B2CLayout>
              <TrackOrder />
            </B2CLayout>
          }
        />

        <Route
          path="/track/:orderId"
          element={
            <B2CLayout>
              <TrackOrder />
            </B2CLayout>
          }
        />

        {/* Demo page for FurnitureOrderTracker component */}
        <Route
          path="/demo/order-tracker"
          element={
            <B2CLayout>
              <FurnitureOrderTrackerDemo />
            </B2CLayout>
          }
        />

        {/* Static pages */}
        <Route
          path="/help"
          element={
            <B2CLayout>
              <Help />
            </B2CLayout>
          }
        />

        <Route
          path="/returns"
          element={
            <B2CLayout>
              <Returns />
            </B2CLayout>
          }
        />

        <Route
          path="/privacy"
          element={
            <B2CLayout>
              <Privacy />
            </B2CLayout>
          }
        />

        <Route
          path="/terms"
          element={
            <B2CLayout>
              <Terms />
            </B2CLayout>
          }
        />

        <Route
          path="/faq"
          element={
            <B2CLayout>
              <FAQ />
            </B2CLayout>
          }
        />

        <Route
          path="/about"
          element={
            <B2CLayout>
              <About />
            </B2CLayout>
          }
        />

        <Route
          path="/delivery"
          element={
            <B2CLayout>
              <Delivery />
            </B2CLayout>
          }
        />

        <Route
          path="/warranty"
          element={
            <B2CLayout>
              <Warranty />
            </B2CLayout>
          }
        />

        <Route
          path="/contacts"
          element={
            <B2CLayout>
              <Contacts />
            </B2CLayout>
          }
        />

        {/* Blog routes */}
        <Route
          path="/blog"
          element={
            <B2CLayout>
              <Blog />
            </B2CLayout>
          }
        />

        <Route
          path="/blog/:slug"
          element={
            <B2CLayout>
              <BlogPost />
            </B2CLayout>
          }
        />

        {/* B2B specific routes */}
        <Route
          path="/b2b/apply"
          element={
            <ProtectedRoute requireAuth={true}>
              <B2CLayout>
                <B2BApplication />
              </B2CLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/b2b/dashboard"
          element={
            <ProtectedRoute requireAuth={true} requireB2B={true}>
              <B2BLayout>
                <B2BDashboard />
              </B2BLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/b2b/quick-order"
          element={
            <ProtectedRoute requireAuth={true} requireB2B={true}>
              <B2BLayout>
                <B2BQuickOrder />
              </B2BLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/b2b/products"
          element={
            <ProtectedRoute requireAuth={true} requireB2B={true}>
              <B2BLayout>
                <ProductCatalog viewMode="b2b" />
              </B2BLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/b2b/products/:slug"
          element={
            <ProtectedRoute requireAuth={true} requireB2B={true}>
              <B2BLayout>
                <ProductDetail viewMode="b2b" />
              </B2BLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
